/* ---------------------------------------------------------------------------
   AddTemplate.tsx
   Renders a searchable / filterable gallery of schema “templates”.
   – Sidebar lets the user browse by type or use-case.
   – Main pane shows cards, with name / description localized via i18next.
--------------------------------------------------------------------------- */

import React, { useMemo, useState } from 'react';
import { useTranslation } from 'localization';
import { useUseCases } from '../../../hooks/useUseCases';

import './AddTemplate.scss';

import { InputText } from 'primereact/inputtext';
import { Button } from 'primereact/button';
import { Accordion, AccordionTab } from 'primereact/accordion';
import { Sidebar } from 'primereact/sidebar';
import {
  PrimeReactSingleSelect,
  TemplatePreview,
} from '../../../design-system-my-lib';
import {
  getEligibleSchemaObjects,
  getSchemaTree,
  getUseCaseTree,
  getTranslation,
  filterHiddenProperties,
} from '@center-platform/spheres/identity';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import { useApiClientsOrNull } from '../../../context/ApiClientsContext';
import { useAvailableTypes } from '../../../hooks/useAvailableTypes';
import { useSchemaType } from '../../../hooks/useSchemaType';

import type { Schema } from 'types/schema';
import type { FilterExpression } from 'types/filters';
import {
  Text,
  Hash,
  CheckSquare,
  Calendar,
  Fingerprint,
  List,
  Braces,
  Filter,
} from 'lucide-react';

/* ---------------------------------------------------------------------------
   Types
--------------------------------------------------------------------------- */
interface TabItem {
  id?: string;
  name?: string;
  description?: string;
  avatar?: string;
  systemName?: string;
  isHeader?: boolean;
  label?: string;
  children?: TabItem[];
  tabType?: 'schema' | 'usecase';
  localization_name_key?: string;
  localization_description_key?: string;
}

interface AddTemplateProps {
  onSphereTypeSelect: (
    sphereType: string,
    source: 'type' | 'usecase',
    label: string
  ) => void;
  useCaseFilter?: string[];
  schemaTypeList?: Schema[];
  useCaseList?: any[];
  assigneeSystemName?: string;
  /**
   * node_label of the assignee (the parent sphere's own type). Pass this from the
   * parent sphere's vertex `.label` so eligibility works even when the parent's own
   * schema can't be resolved via useSchemaType (e.g. no org context, or a system
   * type like center_user that GET /schemas doesn't return). Falls back to the
   * useSchemaType lookup when omitted.
   */
  assigneeNodeLabel?: string;
  onUseTemplate?: (schema: any) => void;
  onTemplateSelect?: (schemaType: string, approachSystemName: string) => void;
  showPreviewButton?: boolean;
  perspectiveSystemNames?: string[];     // Filter schemas by required perspectives
  propertyFilters?: FilterExpression[];  // Filter schemas by property values
  filterGroups?: any[];                   // Filter schemas using OR/AND logic
  sphereId?: string;                      // When provided, fetches types from deployed approaches
}
const propertyTypeIconMap: Record<
  string,
  React.ComponentType<{ size?: string | number }>
> = {
  string: Text,
  number: Hash,
  boolean: CheckSquare,
  date: Calendar,
  uuid: Fingerprint,
  enum: List,
  json: Braces,
};

/** Single property line in the preview pane */
function PropertyRow({
  property,
  i18n,
}: Readonly<{
  property: any;
  i18n: import('i18next').i18n;
}>) {
  const Icon = propertyTypeIconMap[property.type] || Braces;

  return (
    <div className="schema-property-row flex gap-3 align-items-start mb-3">
      <div className="pt-1">
        <Icon size={18} aria-label={property.type} />
      </div>
      <div>
        <div className="font-semibold">
          {getTranslation(i18n, property.label_key, property.label ?? '—')}
        </div>
        <div style={{ color: 'var(--secondary-text-color)' }}>
          {getTranslation(
            i18n,
            property.description_key,
            property.description ?? '—'
          )}
        </div>
      </div>
    </div>
  );
}

/** List of all properties for the template preview */
export function PropertyList({
  properties,
  i18n,
}: Readonly<{
  properties: any[];
  i18n: import('i18next').i18n;
}>) {
  if (!properties?.length) return null;

  // Filter out hidden properties
  const visibleProperties = filterHiddenProperties(properties);

  if (!visibleProperties.length) return null;

  return (
    <div className="schema-properties-list">
      <h4 className="mb-1 mt-1">
        {i18n.t('addTemplate.Properties', { defaultValue: 'Properties' })}
      </h4>
      {visibleProperties.map((prop, idx) => (
        <PropertyRow key={prop.name ?? idx} property={prop} i18n={i18n} />
      ))}
    </div>
  );
}

/* ---------------------------------------------------------------------------
   Helper: Apply property filters to schemas
--------------------------------------------------------------------------- */
const applyPropertyFilters = (
  schemas: any[],
  propertyFilters?: FilterExpression[]
): any[] => {
  if (!propertyFilters || propertyFilters.length === 0) {
    return schemas;
  }

  const filtered = schemas.filter(schemaResult => {
    const schema = schemaResult.schema || schemaResult;

    // All filters must pass for schema to be included
    const passed = propertyFilters.every(filter => {
      let defaultValue;

      // Special handling for top-level schema fields (system_name, node_label, etc.)
      if (filter.property === 'system_name' || filter.property === 'node_label') {
        defaultValue = schema[filter.property];
      } else {
        // Find property definition in schema.properties array
        const property = schema.properties?.find(
          (p: any) => p.name === filter.property
        );

        if (!property) {
          // If property doesn't exist on schema, filter fails
          return false;
        }

        defaultValue = property.default_value;
      }

      // Evaluate filter based on operator
      let result = false;
      switch (filter.operator) {
        case 'equals':
          // Handle both single value and array of values
          if (Array.isArray(filter.values)) {
            result = filter.values.includes(defaultValue);
          } else {
            result = defaultValue === filter.value;
          }
          break;
        case 'not_equals':
          // Handle both single value and array of values
          if (Array.isArray(filter.values)) {
            result = !filter.values.includes(defaultValue);
          } else {
            result = defaultValue !== filter.value;
          }
          break;
        case 'in':
          result = Array.isArray(filter.values) && filter.values.includes(defaultValue);
          break;
        case 'contains':
          result = typeof defaultValue === 'string' &&
                 typeof filter.value === 'string' &&
                 defaultValue.includes(filter.value);
          break;
        case 'greater_than':
          result = defaultValue > filter.value;
          break;
        case 'less_than':
          result = defaultValue < filter.value;
          break;
        case 'greater_than_equals':
          result = defaultValue >= filter.value;
          break;
        case 'less_than_equals':
          result = defaultValue <= filter.value;
          break;
        case 'between':
          result = defaultValue >= (filter.min ?? -Infinity) &&
                 defaultValue <= (filter.max ?? Infinity);
          break;
        case 'exists':
          result = defaultValue !== null && defaultValue !== undefined;
          break;
        case 'is_true':
          result = defaultValue === true;
          break;
        case 'is_false':
          result = defaultValue === false;
          break;
        default:
          console.warn(`[AddTemplate] Unknown filter operator: ${filter.operator}`);
          result = true;
      }

      return result;
    });

    return passed;
  });

  return filtered;
};

/**
 * Apply filterGroups (OR/AND logic) to schemas
 */
const applyFilterGroups = (
  schemas: any[],
  filterGroups?: any[]
): any[] => {
  if (!filterGroups || filterGroups.length === 0) {
    return schemas;
  }

  const filtered = schemas.filter(schemaResult => {
    const schema = schemaResult.schema || schemaResult;

    // All filter groups must pass (AND between groups)
    const passed = filterGroups.every(group => {
      if (group.operator === 'or') {
        // At least one filter in the group must match
        return group.filters.some((filter: any) => {
          let defaultValue;

          // Get value from schema
          if (filter.property === 'system_name' || filter.property === 'node_label') {
            defaultValue = schema[filter.property];
          } else {
            const property = schema.properties?.find((p: any) => p.name === filter.property);
            if (!property) return false;
            defaultValue = property.default_value;
          }

          // Check match based on operator
          let match = false;
          if (filter.operator === 'equals') {
            match = Array.isArray(filter.values) ? filter.values.includes(defaultValue) : defaultValue === filter.value;
          } else if (filter.operator === 'in') {
            match = Array.isArray(filter.values) && filter.values.includes(defaultValue);
          }

          return match;
        });
      } else {
        // All filters in the group must match (AND)
        return group.filters.every((filter: any) => {
          let defaultValue;

          if (filter.property === 'system_name' || filter.property === 'node_label') {
            defaultValue = schema[filter.property];
          } else {
            const property = schema.properties?.find((p: any) => p.name === filter.property);
            if (!property) return false;
            defaultValue = property.default_value;
          }

          let match = false;
          if (filter.operator === 'equals') {
            match = Array.isArray(filter.values) ? filter.values.includes(defaultValue) : defaultValue === filter.value;
          } else if (filter.operator === 'in') {
            match = Array.isArray(filter.values) && filter.values.includes(defaultValue);
          }

          return match;
        });
      }
    });

    return passed;
  });

  return filtered;
};

/* ---------------------------------------------------------------------------
   Component
--------------------------------------------------------------------------- */
const AddTemplate: React.FC<AddTemplateProps> = React.memo(({
  onSphereTypeSelect,
  useCaseFilter,
  schemaTypeList,
  useCaseList,
  assigneeSystemName,
  assigneeNodeLabel,
  onUseTemplate,
  onTemplateSelect,
  showPreviewButton = true,
  perspectiveSystemNames,
  propertyFilters,
  filterGroups,
  sphereId,
}) => {
  /* -----------------------------------------------------------------------
     Hooks & state
  ----------------------------------------------------------------------- */
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery('(max-width: 768px)');
  const apiClients = useApiClientsOrNull();

  // Fetch available types from deployed approaches when sphereId is provided
  const {
    schemas: availableTypeSchemas,
    approachSources,
    templates: approachTemplates,
    assigneeNodeLabel: hookAssigneeNodeLabel,
    loading: availableTypesLoading,
  } = useAvailableTypes(sphereId ? apiClients : null, sphereId, {
    includeTemplates: true,
  });

  const { useCases: hookUseCases } = useUseCases();

  // Schemas come exclusively from useAvailableTypes (scoped to deployed approaches).
  // No Redux fallback — when sphereId is absent, availableTypeSchemas is empty.
  const schemas = schemaTypeList ?? availableTypeSchemas;
  const allUseCases = useCaseList ?? hookUseCases;

  // The eligibility filter needs the parent/assignee schema's node_label to match
  // label-based perspectives. The assignee's own type usually isn't among the
  // available (deployed-approach-provided) types, so resolve it directly and thread
  // its node_label through — without adding it to the creatable candidate list.
  // Without this, getEligibleSchemaObjects bails and the grid shows "No results".
  const { schema: assigneeSchema } = useSchemaType(assigneeSystemName);
  // Prefer the explicit node_label prop (passed from the parent sphere's vertex
  // label) — it's org-independent. Fall back to the useSchemaType lookup, which
  // needs an organizationId + a fetchable schema and so can't resolve the label for
  // org-less users or system types like center_user.
  const resolvedAssigneeNodeLabel =
    assigneeNodeLabel ?? hookAssigneeNodeLabel ?? assigneeSchema?.node_label;
  const assigneeOptions = useMemo(() => {
    const opts: { assigneeSystemName?: string; assigneeNodeLabel?: string } = {};
    if (assigneeSystemName) opts.assigneeSystemName = assigneeSystemName;
    if (resolvedAssigneeNodeLabel) opts.assigneeNodeLabel = resolvedAssigneeNodeLabel;
    return opts;
  }, [assigneeSystemName, resolvedAssigneeNodeLabel]);

  // Filter use cases to only those referenced by the available schemas
  const useCasesList = useMemo(() => {
    if (!schemas || schemas.length === 0) return allUseCases ?? [];
    const referencedTags = new Set<string>();
    for (const s of schemas) {
      if (s.use_case_tags) {
        for (const tag of s.use_case_tags) {
          referencedTags.add(tag);
        }
      }
    }
    if (referencedTags.size === 0) return [];
    return (allUseCases ?? []).filter((uc: any) => referencedTags.has(uc.system_name));
  }, [schemas, allUseCases]);

  // Approach filter: extract unique approach names from approachSources
  const [selectedApproach, setSelectedApproach] = useState<string | null>(null);

  const approachOptions = useMemo(() => {
    if (!sphereId || Object.keys(approachSources).length === 0) return [];
    const approachSet = new Set<string>();
    Object.values(approachSources).forEach(approaches => {
      approaches.forEach(name => approachSet.add(name));
    });
    return Array.from(approachSet).sort((a, b) => a.localeCompare(b));
  }, [sphereId, approachSources]);

  // Show approach filter only when there are 2+ approaches providing types
  const showApproachFilter = approachOptions.length >= 2;

  // Filter schemas by selected approach
  const schemasForDisplay = useMemo(() => {
    if (!selectedApproach || !sphereId) return schemas;
    return schemas.filter((s: any) => {
      const sysName = s.system_name;
      const sources = approachSources[sysName];
      return sources?.includes(selectedApproach);
    });
  }, [schemas, selectedApproach, sphereId, approachSources]);

  type SortOption = 'recommended' | 'name' | 'newest';
  const [sortOption, setSortOption] = useState<SortOption>('recommended');
  const sortOptions = [
    {
      name: t('addTemplate.sort.recommended', { defaultValue: 'Recommended' }),
      code: 'recommended',
    },
    {
      name: t('addTemplate.sort.name', { defaultValue: 'Name' }),
      code: 'name',
    },
    {
      name: t('addTemplate.sort.newest', { defaultValue: 'Newest' }),
      code: 'newest',
    },
  ];

  const [activeSidebar, setActiveSidebar] = useState<'all' | 'accordion'>(
    'all'
  );
  const [accordionIndex, setAccordionIndex] = useState<number[] | null>([0, 1]);
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const [activeChildTab, setActiveChildTab] = useState<number | null>(-1);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [previewSchema, setPreviewSchema] = useState<any>(null);
  const [matchedUseCases, setMatchedUseCases] = useState<any[]>([]);
  const [showFilterSidebar, setShowFilterSidebar] = useState(false);

  const keyword = searchKeyword.trim().toLowerCase();

  // Helper to count active filters
  const activeFilterCount = useMemo(() => {
    let count = 0;
    // Check if a specific sidebar item is selected (not "all")
    if (activeSidebar === 'accordion' && activeIndex !== null) {
      count += 1;
    }
    // Check if search keyword is active
    if (searchKeyword.trim()) {
      count += 1;
    }
    return count;
  }, [activeSidebar, activeIndex, searchKeyword]);

  /* -----------------------------------------------------------------------
     Helpers
  ----------------------------------------------------------------------- */
  const toTabItemTree = (
    nodes: any[],
    tabType: 'schema' | 'usecase' = 'schema'
  ): TabItem[] =>
    (nodes ?? []).map((node) => ({
      id: node.id,
      systemName: node.systemName,
      name: node.name,
      description: node.description,
      avatar: node.avatar,
      tabType,
      children: toTabItemTree(node.children, tabType),
      localization_description_key: node.localization_description_key,
      localization_name_key: node.localization_name_key,
    }));

  /* -----------------------------------------------------------------------
     Helper to filter schema tree nodes that have no visible schemas
  ----------------------------------------------------------------------- */
  const filterSchemaTreeWithVisibleSchemas = (nodes: any[], visibleSchemas: any[]): any[] => {
    const visibleSchemaIds = new Set(visibleSchemas.map(s => s.schema_id || s.id));
    
    return nodes
      .map(node => {
        // Recursively filter children first
        const filteredChildren = filterSchemaTreeWithVisibleSchemas(node.children || [], visibleSchemas);
        
        // If this is a concrete (non-abstract) schema, check if it's visible
        if (!node.isAbstract) {
          const isVisible = visibleSchemaIds.has(node.id);
          return isVisible ? { ...node, children: filteredChildren } : null;
        }
        
        // If this is abstract, only include it if it has visible children
        if (filteredChildren.length > 0) {
          return { ...node, children: filteredChildren };
        }
        
        return null;
      })
      .filter(node => node !== null);
  };

  /* -----------------------------------------------------------------------
     Template datasets
  ----------------------------------------------------------------------- */
  const allTemplates = useMemo(() => {
    const filterObject = {
      ...assigneeOptions,
      ...(perspectiveSystemNames && perspectiveSystemNames.length > 0
        ? { perspectiveSystemNames, enforcePerspectiveMatch: true }
        : {}),
    };

    // getEligibleSchemaObjects needs the assignee's own schema in the input
    // to resolve perspective/assignee matching. If the assignee type isn't
    // already in the available types, temporarily include it from the
    // available schemas list.
    let eligibilityInput = schemasForDisplay;
    if (assigneeSystemName && schemas) {
      const hasAssignee = schemasForDisplay.some(
        (s: any) => s.system_name === assigneeSystemName
      );
      if (!hasAssignee) {
        const assigneeSchema = schemas.find(
          (s: any) => s.system_name === assigneeSystemName
        );
        if (assigneeSchema) {
          eligibilityInput = [...schemasForDisplay, assigneeSchema];
        }
      }
    }

    const results = getEligibleSchemaObjects(eligibilityInput, filterObject);

    // Apply property filters
    let filtered = applyPropertyFilters(results, propertyFilters);

    // Apply filter groups
    filtered = applyFilterGroups(filtered, filterGroups);

    const final = filtered.filter(
      (r) =>
        !keyword ||
        r.schema.name?.toLowerCase().includes(keyword) ||
        r.schema.description?.toLowerCase().includes(keyword)
    );

    return final;
  }, [schemasForDisplay, keyword, assigneeOptions, perspectiveSystemNames, propertyFilters, filterGroups]);

  /* -----------------------------------------------------------------------
     Sidebar items
  ----------------------------------------------------------------------- */
  const items = useMemo(() => {
    // Get the raw schema tree
    const rawSchemaTree = getSchemaTree(schemasForDisplay, 3)[0]?.children ?? [];
    
    // TODO: Re-enable filtering once we solve the data mapping issue
    // For now, show all schema types to avoid empty By Type menu
    const schemaItems = toTabItemTree(
      rawSchemaTree,
      'schema'
    );
    const useCaseItems = toTabItemTree(
      getUseCaseTree(useCasesList, 2),
      'usecase'
    );

    return [
      { isHeader: true, label: 'BY TYPE' },
      ...schemaItems,
      { isHeader: true, label: 'BY USE CASE' },
      ...useCaseItems,
    ];
  }, [schemasForDisplay, useCasesList]);

  const byTypeHeaderIndex = items.findIndex(
    (i) => i.isHeader && i.label === 'BY TYPE'
  );
  const byUseCaseHeaderIndex = items.findIndex(
    (i) => i.isHeader && i.label === 'BY USE CASE'
  );

  const byTypeTabs = items.slice(byTypeHeaderIndex + 1, byUseCaseHeaderIndex);
  const byUseCaseTabs = items.slice(byUseCaseHeaderIndex + 1);

  /* -----------------------------------------------------------------------
     Selection & filtering
  ----------------------------------------------------------------------- */
  const selectedItem = activeIndex !== null ? items[activeIndex] : null;
  const isUseCaseTab = selectedItem?.tabType === 'usecase';

  /* build use-case tree level 3 */
  const useCaseTreeLevel3 = useMemo(
    () => getUseCaseTree(useCasesList, 3),
    [useCasesList]
  );
  const selectedUseCaseNode = isUseCaseTab
    ? useCaseTreeLevel3.find((u) => u.systemName === selectedItem?.systemName)
    : null;

  const useCaseChildren = selectedUseCaseNode?.children ?? [];


  // Gather systemNames for all children
  const allChildSystemNames = useCaseChildren.map((child) => child.systemName);

  const childTabTemplates = useMemo(() => {
    if (!isUseCaseTab || useCaseChildren.length === 0) return null;

    // "All" tab
    if (activeChildTab === -1) {
      const results = getEligibleSchemaObjects(schemasForDisplay, {
        useCaseSystemNames: allChildSystemNames,
        enforceUseCaseMatch: true,
        ...assigneeOptions,
        ...(perspectiveSystemNames && perspectiveSystemNames.length > 0
          ? { perspectiveSystemNames, enforcePerspectiveMatch: true }
          : {}),
      });

      // Apply property filters
      let filtered = applyPropertyFilters(results, propertyFilters);

      // Apply filter groups
      filtered = applyFilterGroups(filtered, filterGroups);

      return filtered.filter(
        (r) =>
          !keyword ||
          r.schema.name?.toLowerCase().includes(keyword) ||
          r.schema.description?.toLowerCase().includes(keyword)
      );
    }

    // Specific child tab
    if (activeChildTab === null) return null;
    const selectedChild = useCaseChildren[activeChildTab];
    if (!selectedChild) return null;
    const results = getEligibleSchemaObjects(schemasForDisplay, {
      useCaseSystemNames: [selectedChild.systemName],
      enforceUseCaseMatch: true,
      ...assigneeOptions,
      ...(perspectiveSystemNames && perspectiveSystemNames.length > 0
        ? { perspectiveSystemNames, enforcePerspectiveMatch: true }
        : {}),
    });

    // Apply property filters
    let filtered = applyPropertyFilters(results, propertyFilters);

    // Apply filter groups
    filtered = applyFilterGroups(filtered, filterGroups);

    return filtered.filter(
      (r) =>
        !keyword ||
        r.schema.name?.toLowerCase().includes(keyword) ||
        r.schema.description?.toLowerCase().includes(keyword)
    );
  }, [
    isUseCaseTab,
    useCaseChildren,
    activeChildTab,
    schemasForDisplay,
    assigneeOptions,
    keyword,
    allChildSystemNames,
    perspectiveSystemNames,
    propertyFilters,
    filterGroups,
  ]);

  const filteredTemplates = useMemo(() => {
    let filterOptions: Record<string, any> = {};

    if (!selectedItem || selectedItem.isHeader) {
      filterOptions = {
        ...assigneeOptions,
        ...(useCaseFilter?.length ? { useCaseSystemNames: useCaseFilter } : {}),
        ...(perspectiveSystemNames && perspectiveSystemNames.length > 0
          ? { perspectiveSystemNames, enforcePerspectiveMatch: true }
          : {}),
      };
    } else if (selectedItem.tabType === 'schema') {
      filterOptions = {
        rootTypeSystemName: selectedItem.systemName,
        ...assigneeOptions,
        ...(perspectiveSystemNames && perspectiveSystemNames.length > 0
          ? { perspectiveSystemNames, enforcePerspectiveMatch: true }
          : {}),
      };
    } else {
      // use-case tab
      filterOptions = {
        useCaseSystemNames: [selectedItem.systemName],
        enforceUseCaseMatch: true,
        ...assigneeOptions,
        ...(perspectiveSystemNames && perspectiveSystemNames.length > 0
          ? { perspectiveSystemNames, enforcePerspectiveMatch: true }
          : {}),
      };
    }

    const results = getEligibleSchemaObjects(schemasForDisplay, filterOptions);

    // Apply property filters
    let filtered = applyPropertyFilters(results, propertyFilters);

    // Apply filter groups
    filtered = applyFilterGroups(filtered, filterGroups);

    const final = filtered.filter(
      (r) =>
        !keyword ||
        r.schema.name?.toLowerCase().includes(keyword) ||
        r.schema.description?.toLowerCase().includes(keyword)
    );

    return final;
  }, [selectedItem, schemasForDisplay, assigneeOptions, keyword, useCaseFilter, perspectiveSystemNames, propertyFilters, filterGroups]);

  /* -----------------------------------------------------------------------
     Sorting
  ----------------------------------------------------------------------- */
  const sortValue = sortOptions.find((o) => o.code === sortOption)!;
  const sortSchemaResults = (
    arr: any[],
    opt: 'recommended' | 'name' | 'newest'
  ) =>
    arr.slice().sort((a, b) => {
      const aSchema = a.schema ?? a;
      const bSchema = b.schema ?? b;

      if (opt === 'recommended') {
        if ((b.weightScore ?? 0) !== (a.weightScore ?? 0)) {
          return (b.weightScore ?? 0) - (a.weightScore ?? 0);
        }
        return (aSchema.name ?? '').localeCompare(bSchema.name ?? '');
      }

      if (opt === 'name') {
        return (aSchema.name ?? '').localeCompare(bSchema.name ?? '');
      }

      // newest
      return (
        Date.parse(bSchema.created_at ?? '') -
        Date.parse(aSchema.created_at ?? '')
      );
    });

  /* -----------------------------------------------------------------------
     Event handlers
  ----------------------------------------------------------------------- */
  const handleTabClick = (index: number) => {
    setActiveIndex(index);
    setActiveChildTab(null);
    setActiveSidebar('accordion');
    setPreviewSchema(null);
    if (isMobile) setShowFilterSidebar(false);

    const sel = items[index];
    if (sel?.tabType === 'usecase') {
      setActiveChildTab(-1);
    } else {
      setActiveChildTab(null);
    }
    if (!sel?.isHeader && sel.systemName) {
      const src = sel.tabType === 'schema' ? 'type' : 'usecase';
      const label = getTranslation(
        i18n,
        sel.localization_name_key,
        sel.name ?? ''
      );
      onSphereTypeSelect(sel.systemName, src, label);
    }
  };

  const handleSortChange = (e: { value: { code: string } | null }) => {
    type SortOption = 'recommended' | 'name' | 'newest';
    setSortOption((e.value?.code ?? 'recommended') as SortOption);
  };

  /* -----------------------------------------------------------------------
     Render helpers
  ----------------------------------------------------------------------- */
  const renderTab = (tab: TabItem) => {
    const idx = items.findIndex((i) => i.id === tab.id);
    const isActive = idx === activeIndex;

    return (
      <div
        key={tab.id}
        role="button"
        tabIndex={0}
        aria-pressed={isActive}
        onClick={() => handleTabClick(idx)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleTabClick(idx);
            e.preventDefault();
          }
        }}
        className={`tab-header${isActive ? ' active' : ''}`}
        style={{
          cursor: 'pointer',
          padding: '8px 20px',
          background: isActive ? 'var(--sidebar-tab-active-background, rgba(55, 127, 196, 0.1))' : undefined,
          fontWeight: isActive ? 600 : 400,
          borderLeft: isActive ? '4px solid #3579f6' : '4px solid transparent',
        }}
        aria-label={getTranslation(i18n, tab.localization_name_key, tab.name)}
      >
        <div className="flex align-items-center gap-2 p-1">
          <span className="white-space-nowrap">
            {getTranslation(i18n, tab.localization_name_key, tab.name)}
          </span>
        </div>
      </div>
    );
  };
  // Assume schema.use_case_tags exists and is an array

  const getMatchedUseCaseObjects = (
    schema: { use_case_tags: string[] },
    useCasesFromStore: any[]
  ): any[] => {
    if (!Array.isArray(schema?.use_case_tags) || !useCasesFromStore?.length)
      return [];
    return schema.use_case_tags
      .map((tag) => useCasesFromStore.find((uc) => uc.system_name === tag))
      .filter(Boolean);
  };

  const renderCard = (schemaObj: any) => {
    const { schema } = schemaObj;
    const key = schema.system_name; // stable key for i18n look-up

    const label = getTranslation(
      i18n,
      schema.localization_name_key,
      schema.name
    );

    const desc = getTranslation(
      i18n,
      schema.localization_description_key,
      schema.schema_description_default
    );

    return (
      <div className="schema-card" key={schema.schema_id}>
        <div className="schema-card-content">
          <div className="schema-card-header">
            <i
              className={`schema-card-icon ${
                schema.avatar ?? 'center-organization-icon'
              }`}
            />
            <h4>{label}</h4>
          </div>

          <p className="schema-card-description">
            {desc ||
              t('noDescription', { defaultValue: 'No description available.' })}
          </p>

          <div className="schema-card-footer">
            <div className="schema-card-org">
              <i className="center-organization-icon schema-card-org-icon" />
              <span>Center Technologies</span>
            </div>

            <div className="schema-card-actions">
              {showPreviewButton && (
                <Button
                  label={t('addTemplate.preview', { ns: 'schema' })}
                  className="p-button-outlined"
                  onClick={() => {
                    setPreviewSchema(schema);
                    const objs = getMatchedUseCaseObjects(
                      schema,
                      hookUseCases
                    );
                    setMatchedUseCases(objs);
                  }}
                />
              )}

              <Button
                label={t('addTemplate.usetemplate', { ns: 'schema' })}
                className="p-button-success"
                onClick={() => {
                  onUseTemplate?.(schema);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  /* -----------------------------------------------------------------------
     Template grouping helpers
  ----------------------------------------------------------------------- */
  const hasAnyTemplates = sphereId && Object.keys(approachTemplates).length > 0;

  const renderTemplateCard = (
    template: { approachSystemName: string; approachName: string | Record<string, string> },
    schemaSystemName: string
  ) => {
    const name = typeof template.approachName === 'object'
      ? getTranslation(i18n, undefined, template.approachName[i18n.language] || template.approachName['en-US'] || template.approachSystemName)
      : template.approachName || template.approachSystemName;

    return (
      <div
        className="schema-card template-card"
        key={`${schemaSystemName}-${template.approachSystemName}`}
      >
        <div className="schema-card-content">
          <div className="schema-card-header">
            <i className="pi pi-box schema-card-icon template-icon" />
            <h4>{name}</h4>
          </div>

          <p className="schema-card-description">
            {t('addTemplate.templateDescription')}
          </p>

          <div className="schema-card-footer">
            <div className="schema-card-org">
              <i className="center-organization-icon schema-card-org-icon" />
              <span>Center Technologies</span>
            </div>

            <div className="schema-card-actions">
              <Button
                label={t('addTemplate.useTemplate')}
                className="p-button-success"
                onClick={() => {
                  onTemplateSelect?.(schemaSystemName, template.approachSystemName);
                }}
              />
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderTypeGroup = (schemaObj: any) => {
    const { schema } = schemaObj;
    const sysName = schema.system_name;
    const typeTemplates = approachTemplates[sysName] ?? [];

    const label = getTranslation(
      i18n,
      schema.localization_name_key,
      schema.name
    );

    // No templates for this type — render a plain card (existing behavior)
    if (typeTemplates.length === 0) {
      return renderCard(schemaObj);
    }

    // Grouped view: type header + blank card + template cards
    return (
      <div className="template-type-group" key={`group-${schema.schema_id}`}>
        <div className="template-type-group-header">
          <i
            className={`schema-card-icon ${schema.avatar ?? 'center-organization-icon'}`}
          />
          <h3 className="template-type-group-title">{label}</h3>
          <span className="template-type-group-count">
            {t('addTemplate.templateCount', { count: typeTemplates.length })}
          </span>
        </div>

        <div className="template-type-group-cards">
          {/* Blank type card */}
          <div className="schema-card blank-type-card" key={`blank-${schema.schema_id}`}>
            <div className="schema-card-content">
              <div className="schema-card-header">
                <i
                  className={`schema-card-icon ${schema.avatar ?? 'center-organization-icon'}`}
                />
                <h4>{t('addTemplate.blankType', { type: label })}</h4>
              </div>

              <p className="schema-card-description">
                {t('addTemplate.blankTypeDescription')}
              </p>

              <div className="schema-card-footer">
                <div className="schema-card-org">
                  <i className="center-organization-icon schema-card-org-icon" />
                  <span>Center Technologies</span>
                </div>

                <div className="schema-card-actions">
                  {showPreviewButton && (
                    <Button
                      label={t('addTemplate.preview', { ns: 'schema' })}
                      className="p-button-outlined"
                      onClick={() => {
                        setPreviewSchema(schema);
                        const objs = getMatchedUseCaseObjects(
                          schema,
                          hookUseCases
                        );
                        setMatchedUseCases(objs);
                      }}
                    />
                  )}

                  <Button
                    label={t('addTemplate.usetemplate', { ns: 'schema' })}
                    className="p-button-success"
                    onClick={() => {
                      onUseTemplate?.(schema);
                    }}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Template cards */}
          {typeTemplates.map((tmpl) => renderTemplateCard(tmpl, sysName))}
        </div>
      </div>
    );
  };

  const renderCards = (schemaResults: any[]) => {
    if (!hasAnyTemplates) {
      return schemaResults.map(renderCard);
    }
    return schemaResults.map(renderTypeGroup);
  };

  /* -----------------------------------------------------------------------
     Render sidebar content (reusable for desktop and mobile)
  ----------------------------------------------------------------------- */
  const renderSidebarContent = () => (
    <>
      {/* ALL TEMPLATES pseudo-tab */}
      <a
        href="#"
        role="button"
        tabIndex={0}
        className={`sidebar-tab${activeSidebar === 'all' ? ' active' : ''}`}
        style={{
          cursor: 'pointer',
          padding: '14px 20px',
          background: activeSidebar === 'all' ? 'var(--sidebar-tab-active-background)' : undefined,
          fontWeight: activeSidebar === 'all' ? 600 : 400,
          borderLeft:
            activeSidebar === 'all'
              ? '4px solid var(--primary-color, #3579f6)'
              : '4px solid transparent',
          borderBottom: 'var(--primary-border)',
          display: 'block',
          textDecoration: 'none',
          color: 'var(--primary-text-color)',
        }}
        onClick={(e) => {
          e.preventDefault();
          setActiveSidebar('all');
          setActiveIndex(null);
          setPreviewSchema(null);
          if (isMobile) setShowFilterSidebar(false);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            setActiveSidebar('all');
            setActiveIndex(null);
            setPreviewSchema(null);
            if (isMobile) setShowFilterSidebar(false);
          }
        }}
      >
        {t('addTemplate.alltemplate', { ns: 'schema' })}
      </a>

      {/* Accordion navigation */}
      <Accordion
        multiple
        activeIndex={accordionIndex}
        onTabChange={(e) => setAccordionIndex(e.index as number[])}
      >
        <AccordionTab
          header={t('addTemplate.bytype', {
            ns: 'schema',
            defaultValue: 'BY TYPE',
          })}
        >
          {byTypeTabs.map(renderTab)}
        </AccordionTab>

        <AccordionTab
          header={t('addTemplate.byusecase', {
            ns: 'schema',
            defaultValue: 'BY USE CASE',
          })}
        >
          {byUseCaseTabs.map(renderTab)}
        </AccordionTab>
      </Accordion>
    </>
  );

  /* -----------------------------------------------------------------------
     JSX
  ----------------------------------------------------------------------- */
  return (
    <div className={`custom-tabview-wrapper${isMobile ? ' mobile-view' : ''}`}>
      <div className="flex w-full">
        {/* ---------------- Desktop Sidebar ---------------- */}
        {!isMobile && (
          <aside
            className="sidebar-tabs"
            style={{
              width: 260,
              minWidth: 220,
              borderRight: 'var(--primary-border)',
              height: '100%',
              overflowY: 'auto',
            }}
          >
            {renderSidebarContent()}
          </aside>
        )}

        {/* ---------------- Mobile Filter Sidebar (PrimeReact Sidebar) ---------------- */}
        {isMobile && (
          <Sidebar
            visible={showFilterSidebar}
            onHide={() => setShowFilterSidebar(false)}
            position="left"
            className="mobile-filter-sidebar"
            header={
              <div className="mobile-filter-header-content">
                <h3>{t('addTemplate.filters')}</h3>
              </div>
            }
          >
            {renderSidebarContent()}
          </Sidebar>
        )}

        {/* ---------------- Main content ---------------- */}
        <section className="tab-content" style={{ flex: 1 }}>
          {previewSchema &&
            (() => {
              return (
                <TemplatePreview
                  schema={previewSchema}
                  matchedUseCases={matchedUseCases}
                  i18n={i18n}
                  t={t}
                  onClose={() => setPreviewSchema(null)}
                />
              );
            })()}
          {!previewSchema && (
            <>
              {/* Search + sort controls */}
              <div className={`template-search-container${isMobile ? ' mobile' : ''}`}>
                <div className="p-inputgroup template-search flex-1">
                  {isMobile && (
                    <button
                      type="button"
                      className="mobile-filter-button"
                      onClick={() => setShowFilterSidebar(true)}
                      aria-label="Open filters"
                    >
                      <Filter size={20} />
                      {activeFilterCount > 0 && (
                        <span className="filter-badge" aria-label={`${activeFilterCount} filters active`}>
                          {activeFilterCount}
                        </span>
                      )}
                    </button>
                  )}
                  <InputText
                    placeholder={t('addTemplate.search', {
                      defaultValue: 'Search…',
                    })}
                    className="search-input Search-input-box"
                    value={searchKeyword}
                    onChange={(e) => setSearchKeyword(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter')
                        setSearchKeyword(e.currentTarget.value);
                    }}
                  />
                  {searchKeyword && (
                    <button
                      type="button"
                      className="pi pi-times"
                      onClick={() => setSearchKeyword('')}
                      aria-label="Clear search"
                      style={{
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                      }}
                    />
                  )}
                  <Button
                    icon="pi pi-search"
                    className="filter-icon"
                    onClick={() => setSearchKeyword('')}
                  />

                  {!isMobile && (
                    <div className="ml-2 flex align-items-center gap-2">
                      {showApproachFilter && (
                        <div className="flex align-items-center approach-filter-container">
                          <label className="sort-label">
                            {t('addTemplate.approachFilter')}
                          </label>
                          <PrimeReactSingleSelect
                            value={
                              selectedApproach
                                ? { name: selectedApproach, code: selectedApproach }
                                : { name: t('addTemplate.allApproaches'), code: '' }
                            }
                            options={[
                              { name: t('addTemplate.allApproaches'), code: '' },
                              ...approachOptions.map(a => ({ name: a, code: a })),
                            ]}
                            onChange={(e: { value: { code: string } | null }) => {
                              setSelectedApproach(e.value?.code || null);
                            }}
                            placeholder={t('addTemplate.allApproaches')}
                            className="approach-filter-dropdown"
                          />
                        </div>
                      )}
                      <label htmlFor="sortBySelect" className="sort-label">
                        {t('addTemplate.sortby', { defaultValue: 'Sort by:' })}
                      </label>

                      <PrimeReactSingleSelect
                        value={sortValue}
                        options={sortOptions}
                        onChange={handleSortChange}
                        placeholder={t('addTemplate.sortby', { defaultValue: 'Sort by' })}
                        className="sort-by-dropdown"
                      />
                    </div>
                  )}
                </div>

                {/* Mobile sort + approach filter dropdown (separate row) */}
                {isMobile && (
                  <div className="mobile-sort-row">
                    {showApproachFilter && (
                      <div className="flex align-items-center gap-1">
                        <label className="sort-label">
                          {t('addTemplate.approachFilter')}
                        </label>
                        <PrimeReactSingleSelect
                          value={
                            selectedApproach
                              ? { name: selectedApproach, code: selectedApproach }
                              : { name: t('addTemplate.allApproaches'), code: '' }
                          }
                          options={[
                            { name: t('addTemplate.allApproaches'), code: '' },
                            ...approachOptions.map(a => ({ name: a, code: a })),
                          ]}
                          onChange={(e: { value: { code: string } | null }) => {
                            setSelectedApproach(e.value?.code || null);
                          }}
                          placeholder={t('addTemplate.allApproaches')}
                          className="approach-filter-dropdown"
                        />
                      </div>
                    )}
                    <label htmlFor="sortBySelect" className="sort-label">
                      {t('addTemplate.sortby', { defaultValue: 'Sort by:' })}
                    </label>
                    <PrimeReactSingleSelect
                      value={sortValue}
                      options={sortOptions}
                      onChange={handleSortChange}
                      placeholder={t('addTemplate.sortby', { defaultValue: 'Sort by' })}
                      className="sort-by-dropdown"
                    />
                  </div>
                )}
              </div>

              {/* Loading indicator for available types */}
              {sphereId && availableTypesLoading ? (
                <div className="schema-card-list available-types-loading">
                  <i className="pi pi-spin pi-spinner" />
                  {t('addTemplate.loadingTypes')}
                </div>
              ) : null}

              {/* Cards list */}
              {!(sphereId && availableTypesLoading) && activeSidebar === 'all' ? (
                <div className="schema-card-list">
                  {allTemplates.length === 0 ? (
                    <div className="schema-card-empty-state">
                      <i className="pi pi-inbox schema-card-empty-state__icon" />
                      <p className="schema-card-empty-state__text">
                        {t('addTemplate.noresultsfound', {
                          defaultValue: 'No results found',
                        })}
                      </p>
                    </div>
                  ) : (
                    renderCards(sortSchemaResults(allTemplates, sortOption))
                  )}
                </div>
              ) : (
                <>
                  {/* Child tabs inside a use-case */}
                  {Array.isArray(accordionIndex) &&
                    accordionIndex.includes(1) &&
                    isUseCaseTab &&
                    useCaseChildren.length > 0 && (
                      <div
                        className="child-tab-headers"
                        style={{ display: 'flex', gap: 8, margin: '1rem 0' }}
                      >
                        <div
                          role="tab"
                          tabIndex={0}
                          aria-selected={activeChildTab === -1}
                          onClick={() => setActiveChildTab(-1)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              setActiveChildTab(-1);
                              e.preventDefault();
                            }
                          }}
                          className={`child-tab-header${
                            activeChildTab === -1 ? ' active' : ''
                          }`}
                        >
                          {t('addTemplate.childtabAll', {
                            defaultValue: 'All',
                          })}
                        </div>
                        {useCaseChildren.map((child, idx) => (
                          <div
                            key={child.id}
                            role="tab"
                            tabIndex={0}
                            aria-selected={activeChildTab === idx}
                            onClick={() => setActiveChildTab(idx)}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter' || e.key === ' ') {
                                setActiveChildTab(idx);
                                e.preventDefault();
                              }
                            }}
                            className={`child-tab-header${
                              activeChildTab === idx ? ' active' : ''
                            }`}
                          >
                            {getTranslation(
                              i18n,
                              child.localization_name_key,
                              child.name
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                  <div className="schema-card-list">
                    {/* Use-case child tab dataset */}
                    {(() => {
                      // Use-case child tab logic
                      if (
                        Array.isArray(accordionIndex) &&
                        accordionIndex.includes(1) &&
                        isUseCaseTab &&
                        useCaseChildren.length > 0
                      ) {
                        if (activeChildTab === null) {
                          return (
                            <div className="schema-card-empty-state">
                              <i className="pi pi-arrow-up-left schema-card-empty-state__icon" />
                              <p className="schema-card-empty-state__text">
                                {t('selectUseCaseChild', {
                                  defaultValue:
                                    'Please select a sub-category to view templates.',
                                })}
                              </p>
                            </div>
                          );
                        } else if (!childTabTemplates?.length) {
                          return (
                            <div className="schema-card-empty-state">
                              <i className="pi pi-inbox schema-card-empty-state__icon" />
                              <p className="schema-card-empty-state__text">
                                {t('addTemplate.noresultsfound', {
                                  defaultValue: 'No results found',
                                })}
                              </p>
                            </div>
                          );
                        } else {
                          return renderCards(sortSchemaResults(
                            childTabTemplates,
                            sortOption
                          ));
                        }
                      }
                      // Default filtered list
                      if (!filteredTemplates.length) {
                        return (
                          <div className="schema-card-empty-state">
                            <i className="pi pi-inbox schema-card-empty-state__icon" />
                            <p className="schema-card-empty-state__text">
                              {t('addTemplate.noresultsfound', {
                                defaultValue: 'No results found',
                              })}
                            </p>
                          </div>
                        );
                      }
                      return renderCards(sortSchemaResults(
                        filteredTemplates,
                        sortOption
                      ));
                    })()}
                  </div>
                </>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
});

AddTemplate.displayName = 'AddTemplate';

export default AddTemplate;
